import {useEffect, useRef} from 'react'
import clsx from 'clsx'
import {makeStyles} from '@material-ui/core/styles'
import Backdrop from '@material-ui/core/Backdrop'
import IconButton from '@material-ui/core/IconButton'
import CloseIcon from '@material-ui/icons/Close'
import ChevronLeft from '@material-ui/icons/ChevronLeft'
import ChevronRight from '@material-ui/icons/ChevronRight'
import {Swiper, SwiperSlide} from 'swiper/react'
import {
  Pagination,
  EffectFade,
  Swiper as SwiperInstance,
  SwiperOptions,
} from 'swiper'
import {PropertyPhotoType} from 'src/types/property'
import {formattedPhoto} from 'src/utils/picture'
import {stopPropagation} from 'src/utils/dom'
import {Loading, useImageSliderContext} from 'src/context/ImageSliderProvider'
import {globalZIndex} from 'src/utils/styles'
import {useCurrentLocale} from 'src/hooks/locale'
import {getPropertyPhotoAltText} from 'src/utils/property'
import AppTypography from 'src/components/elements/typography/AppTypography'

//todo convert to styles.module.css or tailwind utility classes
const useStyles = makeStyles(({breakpoints, spacing, palette}) => ({
  backdrop: {
    zIndex: globalZIndex.underPriority,
    backgroundColor: palette.common.black,
    color: palette.common.white,
    height: '100%',
    width: '100%',
  },
  backdropContainer: {
    width: '100%',
    height: '100%',
    padding: spacing(3, 0),
    display: 'flex',
    flexDirection: 'column',
    zIndex: globalZIndex.priority,
    [breakpoints.down(900)]: {
      padding: spacing(3, 0, 0, 0),
    },
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    width: 'calc(100% - 40px)',
    maxWidth: 1024,
    margin: 'auto',
    [breakpoints.down(900)]: {
      width: 'calc(100% - 8px)',
    },
  },
  iconButton: {
    backgroundColor: 'transparent',
    '& svg': {
      color: palette.common.white,
    },
  },
  backdropImagesContainer: {
    height: 'auto',
    position: 'relative',
  },
  container: {
    position: 'relative',
    overflow: 'hidden',
    width: 'calc(100% - 40px)',
    maxWidth: 1024,
    flex: 1,
    margin: 'auto',
    [breakpoints.down(900)]: {
      width: 'calc(100% - 8px)',
      paddingBottom: spacing(6),
    },
    '& button': {
      display: 'flex',
    },
  },

  swiperContainer: {
    height: '95%',
  },
  imageSizes: {
    width: '90%',
    margin: '0 auto',
    height: '100%',
    position: 'relative',

    [breakpoints.down(900)]: {
      width: '90%',
    },

    '& img': {
      display: 'block',
      objectFit: 'contain',
      objectPosition: 'center',
      height: '100%',
      width: '100%',
      filter: 'saturate(1.2) brightness(1.03) contrast(1.03)',
    },
  },
  smallArrowButton: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: globalZIndex.abovePriority,
    width: spacing(8),
    height: spacing(8),
    background: 0,
    color: 'white',

    [breakpoints.down(900)]: {
      width: spacing(4),
      height: spacing(4),
    },
    // border: 0,
    '& svg': {
      fontSize: 72,

      [breakpoints.down(900)]: {
        fontSize: 48,
      },
    },
  },
  nextBtn: {
    right: 0,
  },
  prevBtn: {
    left: 0,
  },
  countset: {
    width: spacing(5),
    background: 'rgba(255,255,255,0.8)',
    borderRadius: spacing(3),
    zIndex: globalZIndex.abovePriority,
    textAlign: 'center',
    right: 12,
    bottom: 0,
    position: 'absolute',
    color: 'black',
    fontSize: 12,
    [breakpoints.down(900)]: {
      display: 'none',
      background: 'none',
      color: 'white',
      textAlign: 'center',
      position: 'static',
      left: 0,
      right: 0,
      marginLeft: 'auto',
      marginRight: 'auto',
    },
  },
  imageCaption: {
    position: 'absolute',
    color: 'black',
    fontSize: 12,
    bottom: 0,
    left: 12,
    padding: spacing(0, 1),
    background: 'rgba(255,255,255,0.8)',
    borderRadius: spacing(3),
    zIndex: globalZIndex.abovePriority,
    [breakpoints.down(900)]: {
      background: 'none',
      color: 'white',
      textAlign: 'center',
      position: 'static',
      left: 0,
      right: 0,
      marginLeft: 'auto',
      marginRight: 'auto',
    },
  },
}))

const swiperSettings: SwiperOptions = {
  modules: [Pagination, EffectFade],
  keyboard: false, //we use our custom implementations so false
  navigation: false,
  speed: 150,
  loop: true,
  effect: 'fade',
  slidesPerView: 1,
  fadeEffect: {crossFade: true},
  pagination: {
    dynamicBullets: true,
    dynamicMainBullets: 3, //show 3 bullets max
  },
}

export default function LargeSlider(props: {
  open: boolean
  index: number
  handleClose: () => void
}) {
  const {open, index, handleClose} = props

  if (!open) {
    return null
  }

  return <Content index={index} handleClose={handleClose} />
}

function Content(props: {index: number; handleClose: () => void}) {
  const {index, handleClose} = props

  const {
    images,
    currentIndex,
    setCurrentIndex,
    setLoading,
    loading,
    photoCount,
    loadAllPhotos,
  } = useImageSliderContext()

  const classes = useStyles()
  const ref = useRef<SwiperInstance | null>(null)
  const currentLocale = useCurrentLocale()

  useEffect(() => {
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleNext = () => {
    if (loading) {
      return
    }

    if (photoCount === currentIndex + 1) {
      ref.current?.slideReset(150) //provide speed so its infer
      setCurrentIndex(-1)
      ref.current?.slideTo(-1)
      return
    }

    ref.current?.slideNext()
  }

  const handlePrev = () => {
    if (ref.current?.isBeginning && currentIndex === 0) {
      if (images.length < photoCount && !loading) {
        setLoading(true)
        loadAllPhotos()
          .then(() => {
            ref.current?.slideReset(150) //provide speed so its infer
            setCurrentIndex(photoCount - 1)
            ref.current?.slideTo(photoCount - 1)
            setLoading(false)
          })
          .finally(() => {
            //make sure we stop loading even on error
            setLoading(false)
          })
      } else {
        ref.current?.slideReset(150) //provide speed so its infer
        setCurrentIndex(photoCount - 1)
        ref.current?.slideTo(photoCount - 1)
      }
      return
    }
    ref.current?.slidePrev()
  }

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowRight') {
      handleNext()
    } else if (e.key === 'ArrowLeft') {
      handlePrev()
    } else if (e.key === 'Escape') {
      handleClose()
    }
  }

  useEffect(() => {
    setCurrentIndex(index) //set it in the store
    if (ref.current) {
      ref.current.slideTo(index)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, index])

  if (!images.length) {
    return null
  }
  const image =
    typeof images[currentIndex] !== 'undefined' ? images[currentIndex] : null

  return (
    <Backdrop open className={classes.backdrop} onClick={handleClose}>
      <div className={classes.backdropContainer} onClick={handleClose}>
        <div className={classes.buttonContainer}>
          <IconButton className={classes.iconButton}>
            <CloseIcon />
          </IconButton>
        </div>
        <div className={classes.container} onClick={stopPropagation()}>
          <Swiper
            className={classes.swiperContainer}
            {...swiperSettings}
            onInit={(swiper) => (ref.current = swiper)}
            onSlideChange={(e) => {
              if (!ref.current) return
              setCurrentIndex(ref.current.realIndex)
            }}
          >
            {images.map((image: PropertyPhotoType, imageIndex: number) => {
              const newPhoto = formattedPhoto(image.image, 1024)

              let altText = getPropertyPhotoAltText(image, currentLocale)

              return (
                <SwiperSlide
                  key={imageIndex}
                  className={classes.backdropImagesContainer}
                >
                  <div className={classes.imageSizes}>
                    <img
                      src={newPhoto.url}
                      alt={altText}
                      loading={
                        imageIndex === 0 || index === imageIndex
                          ? 'eager'
                          : 'lazy'
                      }
                      //@ts-ignore
                      fetchpriority={
                        imageIndex === 0 || index === imageIndex
                          ? 'high'
                          : 'low'
                      }
                    />
                  </div>
                </SwiperSlide>
              )
            })}
          </Swiper>
          <CustomArrowButton onClick={handlePrev} />
          <CustomArrowButton isNext onClick={handleNext} />
          {loading && (
            <div className="text-white">
              <Loading />
            </div>
          )}
          <AppTypography variant="body" className={classes.countset}>
            {currentIndex + 1}/{photoCount}
          </AppTypography>
          <Caption image={image} />
        </div>
      </div>
    </Backdrop>
  )
}

function Caption(props: {image: PropertyPhotoType | null}) {
  const {image} = props

  const classes = useStyles()

  if (!image) {
    return null
  }

  if (!image.title) {
    return null
  }

  return (
    <AppTypography variant="body" className={classes.imageCaption}>
      {image.title}
    </AppTypography>
  )
}

function CustomArrowButton(props: {isNext?: boolean; onClick?: () => void}) {
  const {isNext, onClick} = props

  const classes = useStyles()

  const className = clsx(
    classes.smallArrowButton,
    isNext ? classes.nextBtn : classes.prevBtn,
  )

  return (
    <IconButton
      className={className}
      onClick={stopPropagation(onClick)}
      style={{width: 64, height: 64}}
    >
      {isNext ? <ChevronRight /> : <ChevronLeft />}
    </IconButton>
  )
}
